import { formatEther } from "viem";
import { useReadContract, useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import { contractConfigs } from "../web3/contracts";

export function usePreTGEStatus() {
	const { data: tgeComplete, isLoading: isTgeLoading } = useReadContract({
		...contractConfigs.preTgeFarming,
		functionName: "tgeComplete",
	});

	const { data: conversionRate, isLoading: isRateLoading } = useReadContract({
		...contractConfigs.preTgeFarming,
		functionName: "conversionRate",
	});

	return {
		tgeComplete: tgeComplete ?? false,
		conversionRate: conversionRate ?? 0n,
		isLoading: isTgeLoading || isRateLoading,
	};
}

export function useUserPoints(address?: `0x${string}`) {
	const { data, isLoading, error, refetch } = useReadContract({
		...contractConfigs.preTgeFarming,
		functionName: "points",
		args: address ? [address] : undefined,
		query: {
			enabled: !!address,
			refetchInterval: 15_000,
		},
	});

	const { data: hasClaimed, isLoading: isClaimedLoading } = useReadContract({
		...contractConfigs.preTgeFarming,
		functionName: "hasClaimed",
		args: address ? [address] : undefined,
		query: {
			enabled: !!address,
			refetchInterval: 15_000,
		},
	});

	return {
		points: data ?? 0n,
		formattedPoints: data ? formatEther(data) : "0",
		hasClaimed: hasClaimed ?? false,
		isLoading: isLoading || isClaimedLoading,
		error,
		refetch,
	};
}

export function usePointsToVoid(points: bigint) {
	const { data, isLoading } = useReadContract({
		...contractConfigs.preTgeFarming,
		functionName: "pointsToVoid",
		args: points > 0n ? [points] : undefined,
		query: {
			enabled: points > 0n,
		},
	});

	return {
		voidAmount: data ?? 0n,
		formattedVoid: data ? formatEther(data) : "0",
		isLoading,
	};
}

export function useFarmSession(sessionId: bigint) {
	const { data, isLoading, error } = useReadContract({
		...contractConfigs.preTgeFarming,
		functionName: "getSession",
		args: sessionId > 0n ? [sessionId] : undefined,
		query: {
			enabled: sessionId > 0n,
		},
	});

	return {
		session: data,
		isLoading,
		error,
	};
}

export function useStartFarm() {
	const { writeContractAsync, data: hash, isPending, error, reset } = useWriteContract();
	const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
		hash,
	});

	const startFarm = async (
		shipTokenId: bigint,
		systemIndex: number,
		zone: number,
		merkleProof: `0x${string}`[],
	) => {
		return writeContractAsync({
			...contractConfigs.preTgeFarming,
			functionName: "startFarm",
			args: [shipTokenId, systemIndex, zone, merkleProof],
		});
	};

	return {
		startFarm,
		hash,
		isPending: isPending || isConfirming,
		isSuccess,
		error,
		reset,
	};
}

export function useClaimFarm() {
	const { writeContractAsync, data: hash, isPending, error, reset } = useWriteContract();
	const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
		hash,
	});

	const claimFarm = async (sessionId: bigint) => {
		return writeContractAsync({
			...contractConfigs.preTgeFarming,
			functionName: "claimFarm",
			args: [sessionId],
		});
	};

	return {
		claimFarm,
		hash,
		isPending: isPending || isConfirming,
		isSuccess,
		error,
		reset,
	};
}

export function useIsClaimable(sessionId: bigint) {
	const { data, isLoading, error } = useReadContract({
		...contractConfigs.preTgeFarming,
		functionName: "isClaimable",
		args: sessionId > 0n ? [sessionId] : undefined,
		query: {
			enabled: sessionId > 0n,
			refetchInterval: 10_000,
		},
	});

	return {
		isClaimable: data ?? false,
		isLoading,
		error,
	};
}

export function useClaimPoints() {
	const { writeContractAsync, data: hash, isPending, error, reset } = useWriteContract();
	const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
		hash,
	});

	const claimPoints = async () => {
		return writeContractAsync({
			...contractConfigs.preTgeFarming,
			functionName: "claimPoints",
		});
	};

	return {
		claimPoints,
		hash,
		isPending: isPending || isConfirming,
		isSuccess,
		error,
		reset,
	};
}

/** Farm outcome enum values */
export enum FarmOutcome {
	Destroyed = 0,
	Poor = 1,
	Standard = 2,
	Rich = 3,
	Jackpot = 4,
}

export const FarmOutcomeNames: Record<FarmOutcome, string> = {
	[FarmOutcome.Destroyed]: "Destroyed",
	[FarmOutcome.Poor]: "Poor",
	[FarmOutcome.Standard]: "Standard",
	[FarmOutcome.Rich]: "Rich",
	[FarmOutcome.Jackpot]: "Jackpot",
};

export const FarmOutcomeColors: Record<FarmOutcome, string> = {
	[FarmOutcome.Destroyed]: "#e05252", // Warm ember red
	[FarmOutcome.Poor]: "#e08040", // Warm amber-orange
	[FarmOutcome.Standard]: "#d4a020", // Rich honey amber
	[FarmOutcome.Rich]: "#34d399", // Bioluminescent green
	[FarmOutcome.Jackpot]: "#e5a040", // Warm gold
};
